import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Stethoscope, Building2, Users, Megaphone, CreditCard, TrendingUp, Flag, ScrollText, LayoutTemplate, Share2, Ticket, Activity, Lightbulb, Star, UserCog, UserPlus, Eye, SearchX } from "lucide-react";
import ReadOnlyLock from "@/components/ReadOnlyLock";

const tabs = [
  { to: "/admin", label: "Consent log", slug: "consent-log", icon: ShieldCheck, end: true, feature: "consent-log" },
  { to: "/admin/collectors", label: "Collectors", slug: "collectors", icon: Stethoscope, feature: "collectors" },
  { to: "/admin/companies", label: "Companies", slug: "companies", icon: Building2, feature: "collectors" },
  { to: "/admin/customers", label: "Customers", slug: "customers", icon: Users, feature: "customers" },
  { to: "/admin/reviews", label: "Reviews", slug: "reviews", icon: Star, feature: "reviews" },
  { to: "/admin/marketing", label: "Marketing", slug: "marketing", icon: LayoutTemplate, feature: "marketing" },
  { to: "/admin/referrals", label: "Referrals", slug: "referrals", icon: Share2, feature: "referrals" },
  { to: "/admin/promos", label: "Promos", slug: "promos", icon: Ticket, feature: "promos" },
  { to: "/admin/ads", label: "Ads", slug: "ads", icon: Megaphone, feature: "ads" },
  { to: "/admin/payments", label: "Payments", slug: "payments", icon: CreditCard, feature: "payments" },
  { to: "/admin/leads", label: "Lead insights", slug: "leads", icon: TrendingUp, feature: "leads" },
  { to: "/admin/unmatched", label: "Unmatched", slug: "unmatched", icon: SearchX, feature: "leads" },
  { to: "/admin/reports", label: "Abuse", slug: "reports", icon: Flag, feature: "reports" },
  { to: "/admin/audit", label: "Audit log", slug: "audit", icon: ScrollText, feature: "audit" },
  { to: "/admin/monitoring", label: "Monitoring", slug: "monitoring", icon: Activity, feature: "monitoring" },
  { to: "/admin/suggestions", label: "Suggestions", slug: "suggestions", icon: Lightbulb, feature: "suggestions" },
  { to: "/admin/admins", label: "Admins", slug: "admins", icon: UserPlus, superOnly: true },
  { to: "/admin/account", label: "My account", slug: "account", icon: UserCog },
];

export const canAccess = (user, feature) => {
  if (!user || user.role !== "admin") return false;
  if (user.is_super_admin) return true;
  if (!feature) return true;
  return (user.permissions?.[feature] || "none") !== "none";
};

export const firstAccessiblePath = (user) => {
  const t = tabs.find((tab) => {
    if (tab.slug === "account") return false;
    if (tab.superOnly) return !!user?.is_super_admin;
    if (tab.feature) return canAccess(user, tab.feature);
    return false;
  });
  return t ? t.to : "/admin/account";
};

export const AdminTabs = () => {
  const { user } = useAuth();
  const location = useLocation();
  const visible = tabs.filter((t) => {
    if (t.superOnly) return !!user?.is_super_admin;
    if (t.feature) return canAccess(user, t.feature);
    return true;
  });

  // Detect the current section's feature to show a view-only banner for sub-admins.
  let currentFeature = null;
  const path = location.pathname;
  for (const t of tabs) {
    if (!t.feature) continue;
    if (path === t.to || path.startsWith(t.to + "/")) {
      if (!currentFeature || t.to.length > currentFeature.to.length) currentFeature = t;
    }
  }
  const viewOnly = user && user.role === "admin" && !user.is_super_admin && currentFeature
    && (user.permissions?.[currentFeature.feature] === "view");

  return (
    <>
      <div className="flex flex-wrap gap-1 border-b border-[#1E3F33]/10 mb-8" data-testid="admin-tabs">
        {visible.map((t) => (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.end}
            data-testid={`admin-tab-${t.slug}`}
            className={({ isActive }) =>
              `inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
                isActive ? "border-[#CC5A3A] text-[#CC5A3A]" : "border-transparent text-[#5C6B64] hover:text-[#1E3F33]"
              }`
            }
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </NavLink>
        ))}
      </div>
      {viewOnly && (
        <div className="-mt-4 mb-6 flex items-center gap-2 rounded-xl border border-[#CC5A3A]/25 bg-[#CC5A3A]/8 px-4 py-2.5" data-testid="view-only-banner">
          <Eye className="w-4 h-4 text-[#CC5A3A] shrink-0" />
          <span className="text-sm text-[#8a4632]">You have <strong>view-only</strong> access to this section — you can review everything here, but changes are disabled.</span>
        </div>
      )}
      {viewOnly && <ReadOnlyLock key={currentFeature?.feature} />}
    </>
  );
};
